import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash, Upload, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface BannedWord {
  id: string;
  word: string;
  created_at: string;
}

interface BannedWordsListProps {
  bannedWords: BannedWord[];
  onWordsChange: () => void;
}

export default function BannedWordsList({ bannedWords, onWordsChange }: BannedWordsListProps) {
  const [newWord, setNewWord] = useState("");
  const [isAddingWord, setIsAddingWord] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredWords = bannedWords.filter(word => 
    word.word.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addBannedWord = async () => {
    if (!newWord.trim()) return;

    const normalizedWord = newWord.toLowerCase().trim();

    // Check if word already exists in the database
    const { data: existingWord, error: checkError } = await supabase
      .from('banned_words')
      .select('word')
      .ilike('word', normalizedWord)
      .maybeSingle();

    if (checkError) {
      toast.error("Error checking word existence");
      return;
    }

    if (existingWord) {
      toast.error("This word is already in the banned list");
      return;
    }

    setIsAddingWord(true);
    try {
      const { error } = await supabase
        .from('banned_words')
        .insert({ word: normalizedWord });

      if (error) throw error;

      toast.success("Word added to banned list");
      setNewWord("");
      onWordsChange();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsAddingWord(false);
    }
  };

  const removeBannedWord = async (id: string) => {
    try {
      const { error } = await supabase
        .from('banned_words')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success("Word removed from banned list");
      onWordsChange();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/plain') {
      toast.error("Please upload a .txt file");
      return;
    }

    setIsUploading(true);
    try {
      const text = await file.text();
      const words = Array.from(
        new Set(
          text
            .split('\n')
            .map(word => word.trim().toLowerCase())
            .filter(word => word.length > 0)
        )
      );

      if (words.length === 0) {
        toast.error("No valid words found in the file");
        return;
      }

      let addedCount = 0;
      let duplicateCount = 0;

      for (const word of words) {
        const { data: exists } = await supabase
          .from('banned_words')
          .select('id')
          .ilike('word', word)
          .maybeSingle();

        if (exists) {
          duplicateCount++;
          continue;
        }

        const { error } = await supabase
          .from('banned_words')
          .insert({ word });

        if (error) {
          if (error.code === '23505') {
            duplicateCount++;
          } else {
            throw error;
          }
        } else {
          addedCount++;
        }
      }

      if (addedCount > 0) {
        toast.success(
          `Added ${addedCount} new ${addedCount === 1 ? 'word' : 'words'} to the banned list` +
          (duplicateCount > 0 ? ` (${duplicateCount} ${duplicateCount === 1 ? 'word was' : 'words were'} already in the list)` : '')
        );
        onWordsChange();
      } else {
        toast.error("All words from the file are already in the banned list");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Banned Words</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search banned words..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-[300px]"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload List
            </Button>
          </div>
          <div className="flex gap-4">
            <Input
              placeholder="Add new word to ban..."
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addBannedWord()}
            />
            <Button onClick={addBannedWord} disabled={isAddingWord}>
              {isAddingWord ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Word
            </Button>
          </div>
        </div>

        <input
          type="file"
          accept=".txt"
          className="hidden"
          onChange={handleFileUpload}
          ref={fileInputRef}
        />

        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full flex justify-between items-center">
              View Banned Words List
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Word</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWords.map((word) => (
                    <TableRow key={word.id}>
                      <TableCell className="font-medium">{word.word}</TableCell>
                      <TableCell>{new Date(word.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeBannedWord(word.id)}
                        >
                          <Trash className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredWords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground">
                        {searchTerm ? "No matching words found" : "No banned words yet"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
